import React from 'react'
import {
  BookingStatus,
  IBooking
} from '../../../../reducers/bookings/types'

import {
  Box,
  makeStyles,
  Typography,
  Divider,
  Grid,
  Container,
  IconButton,
  Tooltip
} from '@material-ui/core'
import InfoIcon from '@material-ui/icons/Info';

import { useSelector } from 'react-redux'
import { DateTime as d } from "luxon"
import { IAppState } from '../../../../store'

import {
  DATE_TYPE,
  getRetnalTaxAmount,
  getTaxDescription,
  toCurrency
} from '../../../utils'

import {
  getLocalizedDateFormat,
  getLocalizedTaxSyntex
} from '../../../../utils/localized.syntex'
import { useTranslation } from 'react-i18next'
import { getAddonBreakdown, getBookingDuration, getProratedFraction } from './helpers';
import DiscountOffersSection from './DiscountOffersSection';

const useStyles = makeStyles((theme) => ({
  root: {
    background: '#f7f7f7',
    padding: theme.spacing(2),
  },
  card: {
    background: '#fff',
    borderRadius: 10,
    padding: theme.spacing(2),
    border: '1px solid #e0e0e0',
    maxWidth: 700,
    margin: '0 auto',
  },
  header: {
    marginBottom: theme.spacing(1),
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
  },
  sub: {
    fontSize: 13,
    color: '#666',
  },
  infoBlock: {
    background: '#fafafa',
    borderRadius: 6,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  label: {
    fontSize: 12,
    color: '#888',
  },
  value: {
    fontSize: 13,
    fontWeight: 500,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 0',
    fontSize: 14,
  },
  subRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '2px 0',
    fontSize: 13,
    color: '#666',
  },
  divider: {
    margin: theme.spacing(1.5, 0),
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontWeight: 600,
    fontSize: 15,
    marginTop: theme.spacing(1),
  },
  unitPrice: {
    fontSize: 12,
    color: "#666",
    marginLeft: 4
  }
}))

interface IProps {
  booking: IBooking
  onBookingUpdated?: (updated: Partial<IBooking>) => void
}

interface IAddonBreakdown {
  name: string
  displayName?: string
  quantity: number
  rate: number
  hasFixedRate: boolean
  base: number
  tax: number
  total: number
  proratedBase: number
  proratedTax: number
  proratedTotal: number
  taxLabel: string | null
}

const SubscriptionPaymentView: React.FC<IProps> = ({ booking, onBookingUpdated }) => {

  const { t } = useTranslation();
  const classes = useStyles()

  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite)
  const isContractHire = website.organisation.bookingCategories.some((category) => category.isContractHire === true)

  const organisation = website.organisation
  const { locale, currency } = organisation
  const { country } = organisation.address

  const branches = website.branches
  const branch = branches.find((b) => b.id === booking.branchId)

  const authUser = useSelector((state: IAppState) => state.authReducer.user)


  const taxes = branch && branch.addTaxOptIn ? branch.taxes.map((tax) => {
    return {
      code: tax.title,
      type: tax.type,
      rate: tax.rate,
      amount: tax.value,
      isVehicleGroupSpecific: tax.isVehicleGroupSpecific || false,
      vehicleGroups: tax.vehicleGroups || [],
      subdivisionOptIn: tax.subdivisionOptIn,
      subdivisionRate: tax.subdivisionRate,
      subdivisionType: tax.subdivisionType,
      subdivisionValue: tax.subdivisionValue
    }
  }) : [];

  /* ===== CORE CALCULATION ===== */

  const bookingDuration = getBookingDuration(booking)
  const proratedFraction = getProratedFraction(booking)
  const isProrated = booking.paymentDetails?.isProrated && proratedFraction < 1

  const baseSubscription = booking.activeSubscription?.subscriptionAmount || 0
  const discount = booking.paymentDetails?.discountAmount || 0
  // Discounts & Offers: applied to the recurring subscription + addons only,
  // never the one-time enrollmentAmount (see getPaymentDetailsForSubscription
  // in reservation-api) - subtracted once from each cycle's overall total
  // below rather than split across the subscription/addon line items, same
  // simplification the non-subscription Quote table already uses for its
  // own "Coupon Discount" row.
  const offerDiscount = booking.paymentDetails?.offerDiscountAmount || 0

  const fullSubscription = Math.max(0, baseSubscription - discount)

  const proratedSubscription = Math.max(0, Math.round(baseSubscription * proratedFraction) - discount)

  const subscriptionTaxFull = getRetnalTaxAmount(
    authUser?.customer?.taxExempted ? [] : taxes,
    fullSubscription,
    bookingDuration,
    1
  )

  const subscriptionTaxProrated = getRetnalTaxAmount(
    authUser?.customer?.taxExempted ? [] : taxes,
    proratedSubscription,
    bookingDuration,
    1
  )

  const enrollment = booking.activeSubscription?.enrollmentAmount || 0

  const enrollmentTax = getRetnalTaxAmount(
    authUser?.customer?.taxExempted ? [] : taxes,
    enrollment,
    1,
    1
  )

  const addonBreakdowns: IAddonBreakdown[] = booking.addonRequirements
    .map(a =>
      getAddonBreakdown(
        a,
        authUser?.customer?.taxExempted || false,
        proratedFraction,
        booking.paymentDetails.rentalTime
      )
    )
    .filter((a): a is IAddonBreakdown => Boolean(a))

  const recurringAddons = addonBreakdowns.filter((a: IAddonBreakdown) => !a.hasFixedRate);

  const recurringAddonsTotal = recurringAddons.reduce(
    (sum: number, a: IAddonBreakdown) => sum + a.total,
    0
  );

  const fixedAddons = addonBreakdowns.filter((a: any) => a.hasFixedRate);

  const fixedAddonsTotal = fixedAddons.reduce(
    (sum: number, a: any) => sum + a.total,
    0
  );

  const addonsProratedTotal = addonBreakdowns.reduce(
    (sum: number, a: IAddonBreakdown) => sum + a.proratedTotal,
    0
  )

  const insuranceFull = booking.insuranceRate || 0
  const insuranceProrated = Math.round(insuranceFull * proratedFraction)

  const fullTotal =
    fullSubscription +
    subscriptionTaxFull +
    recurringAddonsTotal +
    fixedAddonsTotal +
    insuranceFull +
    (!isProrated ? (enrollment + enrollmentTax) : 0) -
    offerDiscount;

  const proratedTotal =
    proratedSubscription +
    subscriptionTaxProrated +
    addonsProratedTotal +
    insuranceProrated +
    enrollment +
    enrollmentTax -
    offerDiscount

  return (
    <Container className={classes.root}>
      <Typography style={{ textAlign: 'center', fontWeight: 600, marginBottom: 12 }}>
        {t("root:paymentOverview")}
      </Typography>

      <Box className={classes.card}>

        <Box className={classes.header}>
          <Typography className={classes.title}>
            {booking.activeSubscription?.vehicle?.make} {booking.activeSubscription?.vehicle?.model}
          </Typography>
        </Box>

        <Grid container spacing={2} style={{ marginTop: 6 }}>

          {/* SCHEDULE */}
          <Grid item xs={12} md={6}>
            <Box
              style={{
                background: '#fafafa',
                borderRadius: 8,
                padding: '10px 12px',
                border: '1px solid #eee',
                height: '100%',
              }}
            >
              <Typography
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 6,
                  color: '#444',
                }}
              >
                Schedule
              </Typography>

              {/* Row */}
              <Box style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <Box>
                  <Typography style={{ fontSize: 11, color: '#888' }}>
                    {t("root:startDate")}
                  </Typography>
                  <Typography style={{ fontSize: 12.5, fontWeight: 500 }}>
                    {getLocalizedDateFormat(
                      country,
                      booking.activeSubscription.startDate || "",
                      DATE_TYPE.CONDENSED
                    )}
                  </Typography>
                </Box>

                <Box>
                  <Typography style={{ fontSize: 11, color: '#888' }}>
                    {t("root:endDate")}
                  </Typography>
                  <Typography style={{ fontSize: 12.5, fontWeight: 500 }}>
                    {getLocalizedDateFormat(
                      country,
                      booking.activeSubscription.endDate || "",
                      DATE_TYPE.CONDENSED
                    )}
                  </Typography>
                </Box>

                <Box style={{ textAlign: 'right' }}>
                  <Typography style={{ fontSize: 11, color: '#888' }}>
                    Booking Ref
                  </Typography>
                  <Typography style={{ fontSize: 12.5, fontWeight: 500 }}>
                    {booking.referenceNumber}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* CONTRACT DETAILS */}
          <Grid item xs={12} md={6}>
            <Box
              style={{
                background: '#fafafa',
                borderRadius: 8,
                padding: '10px 12px',
                border: '1px solid #eee',
                height: '100%',
              }}
            >
              <Typography
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 6,
                  color: '#444',
                }}
              >
                {t("root:contractDetails")}
              </Typography>

              {/* Row */}
              <Box style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <Box>
                  <Typography style={{ fontSize: 11, color: '#888' }}>
                    {t("root:plan")}
                  </Typography>
                  <Typography style={{ fontSize: 12.5, fontWeight: 500 }}>
                    {booking.activeSubscription?.subscription?.name}
                  </Typography>
                </Box>

                <Box>
                  <Typography style={{ fontSize: 11, color: '#888' }}>
                    {t("root:duration")}
                  </Typography>
                  <Typography style={{ fontSize: 12.5, fontWeight: 500 }}>
                    {booking.activeSubscription.duration} {booking.rateTypeName === "monthly" ? t("root:months") : t("root:weeks")}
                  </Typography>
                </Box>

                <Box style={{ textAlign: 'right' }}>
                  <Typography style={{ fontSize: 11, color: '#888' }}>
                    {t("root:swaps")}
                  </Typography>
                  <Typography style={{ fontSize: 12.5, fontWeight: 500 }}>
                    {booking.activeSubscription?.swapsAllowed || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

        </Grid>

        <Divider className={classes.divider} />
        {/* ===== FIRST CYCLE (ALWAYS) ===== */}
        <Typography className={classes.sectionTitle}>
          {` First ${booking.activeSubscription?.subscription?.rateTypeName === "monthly" ? "Month" : "Week"} Charges`}
          {isProrated && ` (${booking.paymentDetails.proratedBillingDays} days pro-rated)`}
        </Typography>

        <Box className={classes.row}>
          <span>
            <span>{`${isContractHire ? t('root:contractHire') : t('root:subscription')}`}</span>
            <span className={classes.unitPrice}>
              (Unit Price: {toCurrency(baseSubscription, currency, locale)})
            </span>
          </span>
          <span>
            {toCurrency(
              (isProrated ? proratedSubscription : fullSubscription) +
              (isProrated ? subscriptionTaxProrated : subscriptionTaxFull),
              currency,
              locale
            )}
          </span>
        </Box>

        <Box className={classes.subRow}>
          <span></span>
          <span>
            <IconButton size="small" style={{ padding: 2, marginRight: 4 }}>
              <Tooltip
                title={
                  <>
                    {getTaxDescription(taxes, currency, locale).map(title => (
                      <>
                        <span>({title})</span><br />
                      </>
                    ))}
                  </>
                }
                arrow
              >
                <InfoIcon style={{ fontSize: 18 }} />
              </Tooltip>
            </IconButton>

            <span style={{ fontSize: 12 }}>
              {getLocalizedTaxSyntex(country || "")}:{" "}
              {toCurrency(
                isProrated ? subscriptionTaxProrated : subscriptionTaxFull,
                currency,
                locale
              )}
            </span>
          </span>
        </Box>

        {/* Enrollment ALWAYS in first */}
        {enrollment > 0 && (
          <>
            <Box className={classes.row}>
              <span>
                <span>{t("root:enrollmentFee")}</span>
                <span className={classes.unitPrice}>
                  (Unit Price: {toCurrency(enrollment, currency, locale)})
                </span>
              </span>
              <span>{toCurrency(enrollment + enrollmentTax, currency, locale)}</span>
            </Box>

            <Box className={classes.subRow}>
              <span></span>
              <span>
                <IconButton size="small" style={{ padding: 2, marginRight: 4 }}>
                  <Tooltip
                    title={
                      <>
                        {getTaxDescription(taxes, currency, locale).map(title => (
                          <>
                            <span>({title})</span><br />
                          </>
                        ))}
                      </>
                    }
                    arrow
                  >
                    <InfoIcon style={{ fontSize: 18 }} />
                  </Tooltip>
                </IconButton>

                <span style={{ fontSize: 12 }}>
                  {getLocalizedTaxSyntex(country || "")}:{" "}
                  {toCurrency(enrollmentTax, currency, locale)}
                </span>
              </span>
            </Box>
          </>
        )}

        {/* ALL addons in first */}
        {addonBreakdowns.length > 0 && (
          <Typography className={classes.sectionTitle}>
            {t("root:addons")}
          </Typography>
        )}

        {addonBreakdowns.map((a: IAddonBreakdown, i: number) => {
          const total = isProrated ? a.proratedTotal : a.total;
          const tax = isProrated ? a.proratedTax : a.tax;

          return (
            <Box key={i} style={{ marginBottom: 6 }}>
              <Box className={classes.row}>
                <span>
                  <span>{a.displayName || a.name}</span>
                  <span className={classes.unitPrice}>
                    (Unit Price: {toCurrency(a.rate, currency, locale)})
                  </span>
                </span>
                <span>{toCurrency(total, currency, locale)}</span>
              </Box>

              <Box className={classes.subRow}>
                <span style={{ fontSize: 12 }}>
                  Quantity: {a.quantity}
                </span>

                <span>
                  {tax > 0 && (
                    <Box style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
                      <Tooltip title={a.taxLabel || ''} arrow>
                        <InfoIcon style={{ fontSize: 18, marginRight: 4 }} />
                      </Tooltip>
                      {getLocalizedTaxSyntex(country || "")}: {toCurrency(tax, currency, locale)}
                    </Box>
                  )}
                </span>
              </Box>
            </Box>
          );
        })}

        {/* Insurance */}
        {(isProrated ? insuranceProrated : insuranceFull) > 0 && (
          <>
            <Typography className={classes.sectionTitle}>
              {t("root:insurance")}
            </Typography>

            <Box className={classes.row}>
              <span>
                <span>{booking.insurancePolicy?.name}</span>
                {booking?.insurancePercentageRate ? (
                  <span className={classes.unitPrice}>
                    ({booking.insurancePercentageRate}% of {isContractHire ? t('root:contractHire') : t('root:subscription')})
                  </span>
                ) : (
                  <span className={classes.unitPrice}>
                    (Unit Price: {toCurrency(booking.insuranceRate || 0, currency, locale)})
                  </span>
                )}
              </span>

              <span>
                {toCurrency(
                  isProrated ? insuranceProrated : insuranceFull,
                  currency,
                  locale
                )}
              </span>
            </Box>
          </>
        )}

        {offerDiscount > 0 && (
          <Box className={classes.row}>
            <span>
              {t("root:couponDiscount")}
              {booking.appliedOffers?.[0]?.code ? ` (${booking.appliedOffers[0].code})` : ""}
            </span>
            <span>-{toCurrency(offerDiscount, currency, locale)}</span>
          </Box>
        )}

        <Divider className={classes.divider} />

        <Box className={classes.totalRow}>
          <span>Total</span>
          <span>
            {toCurrency(
              isProrated ? proratedTotal : fullTotal,
              currency,
              locale
            )}
          </span>
        </Box>

        <Divider className={classes.divider} />

        {/* ===== SUBSEQUENT (ALWAYS) ===== */}
        <Typography className={classes.sectionTitle}>
          Subsequent Charges
        </Typography>

        <Box className={classes.row}>
          <span>
            <span>{`${isContractHire ? t('root:contractHire') : t('root:subscription')}`}</span>
            <span className={classes.unitPrice}>
              (Unit Price: {toCurrency(baseSubscription, currency, locale)})
            </span>
          </span>
          <span>{toCurrency(fullSubscription + subscriptionTaxFull, currency, locale)}</span>
        </Box>

        <Box className={classes.subRow}>
          <span></span>
          <span>
            <IconButton size="small" style={{ padding: 2, marginRight: 4 }}>
              <Tooltip title={
                <>
                  {getTaxDescription(taxes, currency, locale).map(title => (
                    <>
                      <span>({title})</span><br />
                    </>
                  ))}
                </>
              } arrow>
                <InfoIcon style={{ fontSize: 18 }} />
              </Tooltip>
            </IconButton>

            <span style={{ fontSize: 12 }}>
              {getLocalizedTaxSyntex(country || "")}: {toCurrency(subscriptionTaxFull, currency, locale)}
            </span>
          </span>
        </Box>

        {/* ONLY recurring addons */}
        {recurringAddons.length > 0 && (
          <Typography className={classes.sectionTitle}>
            {t("root:addons")}
          </Typography>
        )}

        {recurringAddons.map((a: IAddonBreakdown, i: number) => (
          <React.Fragment key={i}>
            <Box className={classes.row}>
              <span>
                <span>{a.displayName || a.name}</span>
                <span className={classes.unitPrice}>
                  (Unit Price: {toCurrency(a.rate, currency, locale)})
                </span>
              </span>
              <span>{toCurrency(a.total, currency, locale)}</span>
            </Box>

            <Box className={classes.subRow}>
              <span style={{ fontSize: 12 }}>
                Quantity: {a.quantity}
              </span>
              <span style={{ fontSize: 12 }}>
                {a.quantity} X {toCurrency(a.base, currency, locale)}
              </span>
            </Box>

            <Box className={classes.subRow}>
              <span></span>
              <span>
                <IconButton size="small" style={{ padding: 2, marginRight: 4 }}>
                  <Tooltip title={a.taxLabel || ''} arrow>
                    <InfoIcon style={{ fontSize: 18 }} />
                  </Tooltip>
                </IconButton>
                <span style={{ fontSize: 12 }}>
                  {getLocalizedTaxSyntex(country || "")}: {toCurrency(a.tax, currency, locale)}
                </span>
              </span>
            </Box>
          </React.Fragment>
        ))}

        {insuranceFull > 0 && (
          <>
            <Typography className={classes.sectionTitle}>
              {t("root:insurance")}
            </Typography>

            <Box className={classes.row}>
              <span>
                <span>{booking.insurancePolicy?.name}</span>
                {booking?.insurancePercentageRate ? (
                  <span className={classes.unitPrice}>
                    ({booking.insurancePercentageRate}% of {isContractHire ? t('root:contractHire') : t('root:subscription')})
                  </span>
                ) : (
                  <span className={classes.unitPrice}>
                    (Unit Price: {toCurrency(booking.insuranceRate || 0, currency, locale)})
                  </span>
                )}
              </span>
              <span>{toCurrency(insuranceFull, currency, locale)}</span>
            </Box>
          </>
        )}

        {offerDiscount > 0 && (
          <Box className={classes.row}>
            <span>
              {t("root:couponDiscount")}
              {booking.appliedOffers?.[0]?.code ? ` (${booking.appliedOffers[0].code})` : ""}
            </span>
            <span>-{toCurrency(offerDiscount, currency, locale)}</span>
          </Box>
        )}

        <Divider className={classes.divider} />

        <Box className={classes.totalRow}>
          <span>Total</span>
          <span>{toCurrency(fullSubscription + subscriptionTaxFull + recurringAddonsTotal + insuranceFull - offerDiscount, currency, locale)}</span>
        </Box>

        {!!website.organisation.discountModuleEnabled && booking.status !== BookingStatus.IN_PROGRESS && onBookingUpdated && (
          <>
            <Divider className={classes.divider} />
            <DiscountOffersSection booking={booking} onBookingUpdated={onBookingUpdated} />
          </>
        )}
      </Box>
    </Container>
  )
}

export default SubscriptionPaymentView